/**
 * BlockEditor.tsx
 * Renders the editor UI for a single page block.
 */
import type { PageBlock } from "@/utils/customPageStore";
import { BLOCK_TYPES } from "./pageBuilderUtils";

interface BlockEditorProps {
  block: PageBlock;
  onChange: (b: PageBlock) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isDark: boolean;
  isFirst: boolean;
  isLast: boolean;
}

export default function BlockEditor({
  block,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  isDark,
  isFirst,
  isLast,
}: BlockEditorProps) {
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
    isDark ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500" : "border-gray-200 bg-white text-gray-800"
  }`;
  const cardBg = isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200";

  return (
    <div className={`rounded-xl border p-4 ${cardBg}`}>
      {/* Block header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <i className={`${BLOCK_TYPES.find((b) => b.type === block.type)?.icon ?? "ri-layout-line"} text-pink-400`}></i>
          <span className={`text-xs font-bold uppercase tracking-wide ${subColor}`}>
            {BLOCK_TYPES.find((b) => b.type === block.type)?.label}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onDuplicate} className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-600 text-gray-400" : "hover:bg-gray-200 text-gray-500"}`} title="Duplicate block">
            <i className="ri-file-copy-line text-xs"></i>
          </button>
          <button onClick={onMoveUp} disabled={isFirst} className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors disabled:opacity-30 ${isDark ? "hover:bg-gray-600 text-gray-400" : "hover:bg-gray-200 text-gray-500"}`} title="Move up">
            <i className="ri-arrow-up-line text-xs"></i>
          </button>
          <button onClick={onMoveDown} disabled={isLast} className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors disabled:opacity-30 ${isDark ? "hover:bg-gray-600 text-gray-400" : "hover:bg-gray-200 text-gray-500"}`} title="Move down">
            <i className="ri-arrow-down-line text-xs"></i>
          </button>
          <button onClick={onDelete} className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors text-red-400 hover:bg-red-50 hover:text-red-600" title="Delete block">
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
      </div>

      {/* Heading */}
      {block.type === "heading" && (
        <>
          <div className="mb-2 flex items-center gap-1.5">
            <span className={`text-xs font-semibold ${subColor}`}>Level:</span>
            {(["h1", "h2", "h3", "h4"] as const).map((l) => (
              <button key={l} onClick={() => onChange({ ...block, headingLevel: l })}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${block.headingLevel === l ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <input type="text" value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="Section heading..." className={inputCls} />
        </>
      )}

      {/* Text */}
      {block.type === "text" && (
        <>
          <div className="mb-2 flex items-center gap-1.5">
            <span className={`text-xs font-semibold ${subColor}`}>Style:</span>
            {(["default", "card", "gradient"] as const).map((s) => (
              <button key={s} onClick={() => onChange({ ...block, textStyle: s })}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors capitalize ${block.textStyle === s ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                {s}
              </button>
            ))}
          </div>
          <textarea value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="Write your paragraph text here..." rows={4} className={`${inputCls} resize-none`} />
        </>
      )}

      {/* Image */}
      {block.type === "image" && (
        <div className="space-y-2">
          <input type="text" value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="Paste image URL here..." className={inputCls} />
          <input type="text" value={block.imageAlt ?? ""} onChange={(e) => onChange({ ...block, imageAlt: e.target.value })} placeholder="Caption / alt text (optional)" className={inputCls} />
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`text-xs font-semibold ${subColor}`}>Size:</span>
            {(["full", "large", "medium", "small"] as const).map((sz) => (
              <button key={sz} onClick={() => onChange({ ...block, imageSize: sz })}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors capitalize ${block.imageSize === sz ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                {sz}
              </button>
            ))}
            <span className={`text-xs font-semibold ml-2 ${subColor}`}>Caption:</span>
            <button onClick={() => onChange({ ...block, showCaption: !(block.showCaption !== false) })}
              className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${block.showCaption !== false ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
              {block.showCaption !== false ? "On" : "Off"}
            </button>
          </div>
          {block.content && (
            <>
              <div className="rounded-lg overflow-hidden border border-gray-200 mt-2">
                <img src={block.content} alt={block.imageAlt} className="w-full max-h-48 object-cover" />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <label className={`text-xs font-semibold mb-1 block ${subColor}`}><i className="ri-computer-line mr-1"></i>Desktop position</label>
                  <select value={block.desktopPosition ?? "center center"} onChange={(e) => onChange({ ...block, desktopPosition: e.target.value })} className={`${inputCls} cursor-pointer`}>
                    {["center center","top center","bottom center","center left","center right","top left","top right","bottom left","bottom right"].map((v) => <option key={v} value={v}>{v.replace(" ", " / ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`text-xs font-semibold mb-1 block ${subColor}`}><i className="ri-smartphone-line mr-1"></i>Mobile position</label>
                  <select value={block.mobilePosition ?? "center center"} onChange={(e) => onChange({ ...block, mobilePosition: e.target.value })} className={`${inputCls} cursor-pointer`}>
                    {["center center","top center","bottom center","center left","center right","top left","top right","bottom left","bottom right"].map((v) => <option key={v} value={v}>{v.replace(" ", " / ")}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Image Grid */}
      {block.type === "image-grid" && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`text-xs font-semibold ${subColor}`}>Columns:</span>
            {([2, 3, 4] as const).map((c) => (
              <button key={c} onClick={() => onChange({ ...block, imageCols: c })}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${(block.imageCols ?? 2) === c ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                {c}
              </button>
            ))}
          </div>
          {(block.images ?? []).map((img, i) => (
            <div key={img.id} className="flex items-center gap-2">
              <input type="text" value={img.url} onChange={(e) => { const imgs = (block.images ?? []).map((im, idx) => idx === i ? { ...im, url: e.target.value } : im); onChange({ ...block, images: imgs }); }} placeholder={`Image ${i + 1} URL...`} className={`${inputCls} flex-1`} />
              <input type="text" value={img.alt} onChange={(e) => { const imgs = (block.images ?? []).map((im, idx) => idx === i ? { ...im, alt: e.target.value } : im); onChange({ ...block, images: imgs }); }} placeholder="Alt text" className={`${inputCls} w-32`} />
              <button onClick={() => { const imgs = (block.images ?? []).filter((_, idx) => idx !== i); onChange({ ...block, images: imgs }); }} className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer text-red-400 hover:bg-red-50 hover:text-red-600" title="Remove image">
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ))}
          <button onClick={() => { const imgs = [...(block.images ?? []), { id: `img-${Date.now()}`, url: "", alt: "" }]; onChange({ ...block, images: imgs }); }} className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer">
            <i className="ri-add-line"></i> Add image
          </button>
        </div>
      )}

      {/* Two Column */}
      {block.type === "two-col" && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`text-xs font-semibold ${subColor}`}>Left:</span>
            {(["text", "image"] as const).map((t) => (
              <button key={t} onClick={() => onChange({ ...block, leftType: t })}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${(block.leftType ?? "text") === t ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                {t}
              </button>
            ))}
            <span className={`text-xs font-semibold ml-2 ${subColor}`}>Right:</span>
            {(["text", "image"] as const).map((t) => (
              <button key={t} onClick={() => onChange({ ...block, rightType: t })}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${(block.rightType ?? "text") === t ? "bg-pink-500 text-white" : isDark ? "bg-gray-600 text-gray-400 hover:bg-gray-500" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                {t}
              </button>
            ))}
          </div>
          {(block.leftType ?? "text") === "image" ? (
            <div className="space-y-1">
              <input type="text" value={block.leftImage ?? ""} onChange={(e) => onChange({ ...block, leftImage: e.target.value })} placeholder="Left column image URL..." className={inputCls} />
              <input type="text" value={block.leftImageAlt ?? ""} onChange={(e) => onChange({ ...block, leftImageAlt: e.target.value })} placeholder="Left image alt text..." className={inputCls} />
            </div>
          ) : (
            <textarea value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="Left column text..." rows={3} className={`${inputCls} resize-none`} />
          )}
          {(block.rightType ?? "text") === "image" ? (
            <div className="space-y-1">
              <input type="text" value={block.rightImage ?? ""} onChange={(e) => onChange({ ...block, rightImage: e.target.value })} placeholder="Right column image URL..." className={inputCls} />
              <input type="text" value={block.rightImageAlt ?? ""} onChange={(e) => onChange({ ...block, rightImageAlt: e.target.value })} placeholder="Right image alt text..." className={inputCls} />
            </div>
          ) : (
            <textarea value={block.subContent ?? ""} onChange={(e) => onChange({ ...block, subContent: e.target.value })} placeholder="Right column text..." rows={3} className={`${inputCls} resize-none`} />
          )}
        </div>
      )}

      {/* CTA */}
      {block.type === "cta" && (
        <div className="space-y-2">
          <input type="text" value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="CTA headline..." className={inputCls} />
          <input type="text" value={block.subContent ?? ""} onChange={(e) => onChange({ ...block, subContent: e.target.value })} placeholder="Supporting text (optional)..." className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={block.ctaLabel ?? ""} onChange={(e) => onChange({ ...block, ctaLabel: e.target.value })} placeholder="Button label..." className={inputCls} />
            <input type="text" value={block.ctaUrl ?? ""} onChange={(e) => onChange({ ...block, ctaUrl: e.target.value })} placeholder="Button URL (e.g. /contact)" className={inputCls} />
          </div>
        </div>
      )}

      {/* Image Detail */}
      {block.type === "image-detail" && (
        <div className="space-y-2">
          <input type="text" value={block.content} onChange={(e) => onChange({ ...block, content: e.target.value })} placeholder="Paste image URL here..." className={inputCls} />
          <input type="text" value={block.subContent ?? ""} onChange={(e) => onChange({ ...block, subContent: e.target.value })} placeholder="Image title..." className={inputCls} />
          <textarea value={block.ctaLabel ?? ""} onChange={(e) => onChange({ ...block, ctaLabel: e.target.value })} placeholder="Description text..." rows={3} className={`${inputCls} resize-none`} />
          {block.content && (
            <div className="rounded-lg overflow-hidden border border-gray-200 mt-2">
              <img src={block.content} alt={block.subContent} className="w-full max-h-48 object-cover" />
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      {block.type === "divider" && (
        <div className={`text-xs ${subColor} italic`}>— Horizontal divider / spacer —</div>
      )}

      {/* Section (imported) */}
      {block.type === "section" && (
        <div className="space-y-2">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark ? "border-green-700 bg-green-900/20" : "border-green-200 bg-green-50"}`}>
            <i className="ri-layout-masonry-line text-green-500 text-sm"></i>
            <span className={`text-xs font-bold ${isDark ? "text-green-400" : "text-green-700"}`}>
              Imported Section — {block.sectionMeta?.layout ?? "custom"}
            </span>
          </div>
          <p className={`text-[10px] ${subColor} leading-relaxed`}>
            This block preserves the original section&apos;s layout, colors, cards, images, and styling. Duplicate or delete it — you cannot edit its inner structure here. Edit the text directly on the live page in admin mode.
          </p>
        </div>
      )}

      <div className={`mt-2 text-[10px] font-mono ${subColor}`}>Block ID: {block.id.slice(-8)}</div>
    </div>
  );
}